import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  applyHrLeave,
  grantHrDirectLeave,
  cancelHrLeave,
  fetchHrAttendance,
  fetchHrDirectory,
  fetchHrHolidays,
  fetchHrLeaves,
  fetchLeaveBalances,
  fetchLeaveTypes,
  markHrAttendance,
  markHrPersonalHoliday,
  reviewHrLeave,
  setHrLeaveBalance,
  fetchPresentOnLeaveRequests,
  reviewPresentOnLeave,
} from "../../store/hr/hrSlice";
import { useHrAccess } from "../../hooks/useHrAccess";
import {
  canAdjustLeaveBalanceOf,
  canApplyLeaveRequests,
  canCancelLeaveOf,
  canManageLeaveBalances,
  canReviewLeaveOf,
  canViewTeamLeaveRequests,
  leaveTier,
} from "../../utils/leaveHierarchy";
import { APPLY_LEAVE_CODES, codeOf, remainingOf } from "../../utils/leavePolicy";
import { HrLeaveType } from "../../types/HrTypes";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminPrimaryButton,
  AdminSelect,
  AdminSoftInput,
  AdminSoftSelect,
  AdminTableCard,
  AdminTableState,
  adminHeaderActionClass,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";
import { CustomDatePicker } from "../../components/common/DatePickers";
import { LeaveMonthCalendar } from "../../components/common/LeaveMonthCalendar";
import { monthAttendanceFetchRange } from "../../components/common/AttendanceMonthCalendar";
import { IST_TIMEZONE } from "../../utils/dateTime";
import { isWeekOffDate, normalizeWeekOffDays } from "../../utils/weekOff";

function todayIst() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function formatLeaveDate(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function parseLeaveStamp(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLeaveRequestedDate(value?: string | null) {
  const d = parseLeaveStamp(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatLeaveRequestedTime(value?: string | null) {
  const d = parseLeaveStamp(value);
  if (!d) return "—";
  return d.toLocaleTimeString("en-IN", {
    timeZone: IST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const Leaves = () => {
  const dispatch = useAppDispatch();
  const { isHrAdmin, isSuperAdmin, empId, user, employee, canMarkAttendance } = useHrAccess();
  const { leaves, leaveTypes, balances, directory, presentRequests, attendance, holidays, loading, error } =
    useAppSelector((state) => state.hr);
  const verifiedDirectory = directory.filter((e) => e.verified);
  const [status, setStatus] = useState(isHrAdmin ? "pending" : "");
  const [leaveType, setLeaveType] = useState("");
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState("");
  const [applyEmp, setApplyEmp] = useState(empId ? String(empId) : "");
  const [grantType, setGrantType] = useState("");
  const [grantDays, setGrantDays] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayHalf, setHolidayHalf] = useState(false);
  const [weekOffDate, setWeekOffDate] = useState("");
  const [weekOffNote, setWeekOffNote] = useState("");
  const [weekOffBusy, setWeekOffBusy] = useState(false);
  const selectedEmployee = verifiedDirectory.find((e) => String(e.emp_id) === applyEmp);
  const applyWeekOffDays = useMemo(() => {
    const fromDirectory =
      selectedEmployee?.profile?.week_off_days ??
      verifiedDirectory.find((e) => e.emp_id === empId)?.profile?.week_off_days;
    return normalizeWeekOffDays(fromDirectory);
  }, [selectedEmployee, verifiedDirectory, empId]);
  const balanceActor = { portalRole: user?.role, empRole: employee?.role, empId };
  const managesBalances = canManageLeaveBalances(balanceActor);
  const canViewTeamLeaves = canViewTeamLeaveRequests(balanceActor);
  const canApplyLeave = canApplyLeaveRequests(balanceActor) && Boolean(empId);
  const canEditBalance = Boolean(
    managesBalances &&
      selectedEmployee &&
      canAdjustLeaveBalanceOf(balanceActor, {
        role: selectedEmployee.role,
        emp_id: selectedEmployee.emp_id,
      }),
  );
  const targetEmpId = applyEmp ? Number(applyEmp) : empId;

  const directTargets = useMemo(
    () =>
      verifiedDirectory.filter((e) => {
        if (leaveTier(e.role) === "super_admin") return false;
        return canReviewLeaveOf(balanceActor, { role: e.role, emp_id: e.emp_id });
      }),
    [verifiedDirectory, balanceActor.portalRole, balanceActor.empRole, balanceActor.empId],
  );

  useEffect(() => {
    if (!canApplyLeave && empId && applyEmp === String(empId) && directTargets[0]) {
      setApplyEmp(String(directTargets[0].emp_id));
      return;
    }
    if (applyEmp) return;
    if (canApplyLeave && empId) {
      setApplyEmp(String(empId));
      return;
    }
    if (canViewTeamLeaves && directTargets[0]) {
      setApplyEmp(String(directTargets[0].emp_id));
    }
  }, [empId, applyEmp, canApplyLeave, canViewTeamLeaves, directTargets]);

  const load = () => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchHrLeaves(status ? { status } : undefined));
    dispatch(
      fetchLeaveBalances(
        canViewTeamLeaves
          ? undefined
          : empId
            ? { emp_id: empId }
            : undefined,
      ),
    );
    dispatch(fetchHrHolidays(new Date().getFullYear()));
    if (canViewTeamLeaves || isHrAdmin) {
      dispatch(fetchHrDirectory());
      dispatch(fetchPresentOnLeaveRequests());
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, status, applyEmp, isHrAdmin, empId]);

  useEffect(() => {
    if (!targetEmpId) return;
    // Week-off dates for leave calendar; never fetch future dates.
    const range = monthAttendanceFetchRange(viewMonth);
    if (!range) return;
    dispatch(
      fetchHrAttendance({
        from: range.from,
        to: range.to,
        emp_id: targetEmpId,
      }),
    );
  }, [dispatch, targetEmpId, viewMonth]);

  const employeeBalances = balances.filter((b) =>
    applyEmp
      ? b.emp_id === Number(applyEmp)
      : empId != null && b.emp_id === empId,
  );
  const ownBalances = balances.filter((b) => empId != null && b.emp_id === empId);
  const assignedTypeIds = new Set(employeeBalances.map((b) => b.leave_type_id));
  const remainingByCode = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of ownBalances) {
      map[codeOf(b.leaveType)] = remainingOf(b);
    }
    return map;
  }, [ownBalances]);

  const targetRemainingByCode = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of employeeBalances) {
      map[codeOf(b.leaveType)] = remainingOf(b);
    }
    return map;
  }, [employeeBalances]);

  const applyTypes = leaveTypes.filter(
    (t) => t.is_active && APPLY_LEAVE_CODES.includes(codeOf(t) as (typeof APPLY_LEAVE_CODES)[number]),
  );
  const needed = halfDay
    ? selectedDates.length === 1
      ? 0.5
      : 0
    : selectedDates.length;
  const typeEnabledFor = (remainingMap: Record<string, number>, t: HrLeaveType) => {
    const code = codeOf(t);
    if (code === "UL") return needed > 0;
    if (needed <= 0) return false;
    return (remainingMap[code] || 0) >= needed;
  };
  const holidayDateSet = useMemo(
    () => holidays.map((h) => h.holiday_date),
    [holidays],
  );
  const customWeekOffDates = useMemo(
    () =>
      attendance
        .filter(
          (row) =>
            row.status === "weekend" &&
            (!targetEmpId || row.emp_id === targetEmpId),
        )
        .map((row) => row.attendance_date),
    [attendance, targetEmpId],
  );

  useEffect(() => {
    setSelectedDates((prev) =>
      prev.filter(
        (iso) =>
          !isWeekOffDate(iso, applyWeekOffDays) &&
          !customWeekOffDates.includes(iso),
      ),
    );
  }, [applyWeekOffDays, customWeekOffDates]);

  const calendarMinDate =
    canViewTeamLeaves && applyEmp && String(empId) !== applyEmp ? undefined : todayIst();
  const typeEnabled = (t: HrLeaveType) => typeEnabledFor(remainingByCode, t);
  const directTypeEnabled = (t: HrLeaveType) => typeEnabledFor(targetRemainingByCode, t);

  const upcomingHolidays = holidays.filter((h) => h.holiday_date >= new Date().toISOString().slice(0, 10));

  const grant = async () => {
    if (!applyEmp || !grantType || grantDays === "") return;
    const result = await dispatch(
      setHrLeaveBalance({
        emp_id: Number(applyEmp),
        leave_type_id: Number(grantType),
        allocated: Number(grantDays),
        source: "leaves_assign",
      }),
    );
    if (setHrLeaveBalance.fulfilled.match(result)) {
      setGrantType("");
      setGrantDays("");
      load();
    }
  };

  const apply = async () => {
    if (!canApplyLeave || !empId || !leaveType || selectedDates.length === 0) return;
    if (halfDay && selectedDates.length !== 1) return;
    const result = await dispatch(
      applyHrLeave({
        emp_id: empId,
        leave_type_id: Number(leaveType),
        dates: selectedDates,
        from_date: selectedDates[0],
        to_date: selectedDates[selectedDates.length - 1],
        reason,
        half_day: halfDay,
      }),
    );
    if (applyHrLeave.fulfilled.match(result)) {
      setReason("");
      setHalfDay(false);
      setSelectedDates([]);
      load();
    }
  };

  const grantDirect = async () => {
    if (!canViewTeamLeaves || !applyEmp || !leaveType || selectedDates.length === 0) return;
    if (halfDay && selectedDates.length !== 1) return;
    const target = verifiedDirectory.find((e) => String(e.emp_id) === applyEmp);
    if (!target || !canReviewLeaveOf(balanceActor, { role: target.role, emp_id: target.emp_id })) {
      return;
    }
    const result = await dispatch(
      grantHrDirectLeave({
        emp_id: Number(applyEmp),
        leave_type_id: Number(leaveType),
        dates: selectedDates,
        from_date: selectedDates[0],
        to_date: selectedDates[selectedDates.length - 1],
        reason,
        half_day: halfDay,
      }),
    );
    if (grantHrDirectLeave.fulfilled.match(result)) {
      setReason("");
      setHalfDay(false);
      setSelectedDates([]);
      load();
    }
  };

  const markHoliday = async () => {
    if (!applyEmp || !holidayDate) return;
    const result = await dispatch(
      markHrPersonalHoliday({
        emp_id: Number(applyEmp),
        attendance_date: holidayDate,
        half_day: holidayHalf,
      }),
    );
    if (markHrPersonalHoliday.fulfilled.match(result)) {
      setHolidayDate("");
      setHolidayHalf(false);
      load();
    }
  };

  const markCustomWeekOff = async () => {
    if (!canMarkAttendance || !applyEmp || !weekOffDate) return;
    setWeekOffBusy(true);
    const result = await dispatch(
      markHrAttendance({
        emp_id: Number(applyEmp),
        attendance_date: weekOffDate,
        status: "weekend",
        notes: weekOffNote.trim() || "Custom week off",
      }),
    );
    setWeekOffBusy(false);
    if (markHrAttendance.fulfilled.match(result)) {
      setWeekOffDate("");
      setWeekOffNote("");
      load();
    }
  };

  return (
    <>
      <Helmet>
        <title>Leaves</title>
      </Helmet>
      <AdminPageShell accent="#345952">
        <AdminPageHeader
          title="Leaves"
          subtitle={
            canViewTeamLeaves
              ? "Apply from today. October remaining is what HR entered. On 1 Nov the portal adds PL 1.50 + CL 0.75 (2.25). PL carries forward; CL and Holiday lapse at year end. PL and CL cannot be combined. Managers can also give leave directly."
              : "Apply leave and track your requests."
          }
        >
          <AdminSelect value={status} onChange={setStatus}>
            <option value="">All requests</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </AdminSelect>
          {managesBalances ? (
            <Link
              to="/dashboard/employee/my-attendance"
              className={adminHeaderActionClass}
            >
              Edit balances on Attendance
            </Link>
          ) : null}
          {isSuperAdmin ? (
            <Link
              to="/dashboard/employee/leave-balance-logs"
              className={adminHeaderActionClass}
            >
              Balance edit logs
            </Link>
          ) : null}
        </AdminPageHeader>
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}

        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {["PL", "CL", "HOL", "UL"].map((code) => {
            const row = employeeBalances.find((b) => codeOf(b.leaveType) === code);
            const label =
              code === "PL"
                ? "Privilege Leave"
                : code === "CL"
                  ? "Casual Leave"
                  : code === "HOL"
                    ? "Holiday quota"
                    : "Unpaid used";
            const value = row ? (code === "UL" ? Number(row.used) : remainingOf(row)) : 0;
            return (
              <div key={code} className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-[#8A8A85]">{label}</p>
                <p className="mt-1 text-2xl font-bold text-[#4A4A48]">{value}</p>
                {row && code !== "UL" ? (
                  <p className="text-xs text-[#8A8A85]">
                    {row.allocated} allocated · {row.used} used · {row.pending} pending
                  </p>
                ) : (
                  <p className="text-xs text-[#8A8A85]">Salary deduct when used</p>
                )}
              </div>
            );
          })}
        </div>

        {upcomingHolidays.length ? (
          <p className="mb-4 text-xs text-[#8A8A85]">
            Upcoming company holidays:{" "}
            {upcomingHolidays
              .slice(0, 6)
              .map((h) => `${h.name} (${formatLeaveDate(h.holiday_date)})`)
              .join(" · ")}
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm lg:col-span-1">
            <h2 className="mb-3 text-sm font-semibold">
              {canViewTeamLeaves ? "Leave for team / self" : "Apply leave"}
            </h2>
            {!canApplyLeave && canViewTeamLeaves ? (
              <p className="mb-3 text-xs leading-5 text-[#8A8A85]">
                Super Admin does not apply leave for themselves. Review requests and give leave
                directly to employees.
              </p>
            ) : null}
            {canViewTeamLeaves ? (
              <label className="mb-3 block text-xs text-[#8A8A85]">
                Employee
                <AdminSoftSelect
                  className="mt-1 block w-full"
                  value={applyEmp}
                  onChange={(e) => setApplyEmp(e.target.value)}
                >
                  {canApplyLeave && empId ? (
                    <option value={String(empId)}>
                      {employee?.emp_name || "Me"} (my request)
                    </option>
                  ) : (
                    <option value="">Select employee</option>
                  )}
                  {directTargets.map((e) => (
                    <option key={e.emp_id} value={e.emp_id}>
                      {e.emp_name}
                      {e.role ? ` · ${e.role}` : ""}
                    </option>
                  ))}
                </AdminSoftSelect>
              </label>
            ) : null}
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-xs text-[#4A4A48]">
                <input
                  type="checkbox"
                  checked={halfDay}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setHalfDay(on);
                    if (on && selectedDates.length > 1) {
                      setSelectedDates([selectedDates[0]]);
                    }
                  }}
                  className="rounded border-[#DDDDD8]"
                />
                Half day (0.5)
              </label>
              <LeaveMonthCalendar
                yearMonth={viewMonth}
                onYearMonthChange={setViewMonth}
                selected={selectedDates}
                onChange={setSelectedDates}
                minDate={calendarMinDate}
                holidayDates={holidayDateSet}
                weekOffDays={applyWeekOffDays}
                extraWeekOffDates={customWeekOffDates}
                halfDay={halfDay}
              />
              <p className="text-xs text-[#8A8A85]">
                {needed <= 0
                  ? "Select leave day(s) on the calendar."
                  : canViewTeamLeaves
                    ? `${needed} day(s) selected. PL and CL cannot be combined.`
                    : `${needed} day(s) selected.`}
              </p>
              <div className="space-y-1.5">
                {applyTypes.map((t) => {
                  const forSelf = !canViewTeamLeaves || String(empId) === applyEmp;
                  const enabled = forSelf ? typeEnabled(t) : directTypeEnabled(t);
                  const code = codeOf(t);
                  return (
                    <label
                      key={t.leave_type_id}
                      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${
                        enabled ? "border-[#E8E8E4]" : "border-[#F0F0EC] text-[#8A8A85]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="leave-type"
                        disabled={!enabled}
                        checked={leaveType === String(t.leave_type_id)}
                        onChange={() => setLeaveType(String(t.leave_type_id))}
                        className="mt-1"
                      />
                      <span className="font-medium">{t.name}</span>
                      {!enabled && code !== "UL" ? (
                        <span className="ml-2 text-xs text-[#8A8A85]">Not enough balance</span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              <textarea
                placeholder="Note (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="rounded-xl border border-[#DDDDD8] bg-[#FDFBF2] px-3 py-2 text-sm outline-none"
              />
              {canApplyLeave && (!canViewTeamLeaves || String(empId) === applyEmp) ? (
                <AdminPrimaryButton
                  onClick={apply}
                  disabled={!leaveType || needed <= 0}
                >
                  {halfDay ? "Submit my half day request" : "Submit my leave request"}
                </AdminPrimaryButton>
              ) : null}
              {canViewTeamLeaves && applyEmp && String(empId) !== applyEmp ? (
                <AdminPrimaryButton
                  onClick={grantDirect}
                  disabled={!leaveType || needed <= 0}
                >
                  {halfDay ? "Give half day leave directly" : "Give leave directly"}
                </AdminPrimaryButton>
              ) : null}
              {canViewTeamLeaves && !applyEmp ? (
                <p className="text-xs text-[#8A8A85]">Select an employee to give leave directly.</p>
              ) : null}
            </div>
            {managesBalances ? (
              <p className="mt-4 text-[11px] leading-4 text-[#8A8A85]">
                Balance totals are shown in the cards above. To change PL, CL, Holiday, or Unpaid,
                open the employee on Attendance.
              </p>
            ) : null}
            {isHrAdmin && !applyEmp ? (
              <p className="mt-4 text-xs text-[#8A8A85]">
                Select an employee to see their leave balances above.
              </p>
            ) : null}
            {applyEmp && employeeBalances.length === 0 ? (
              <p className="mt-4 text-xs text-[#8A8A85]">
                No leave types assigned to this employee.
              </p>
            ) : null}
            {canEditBalance ? (
              <>
                <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-[#E8E8E4] pt-3">
                  <AdminSoftSelect
                    value={grantType}
                    onChange={(e) => {
                      setGrantType(e.target.value);
                      const type = leaveTypes.find(
                        (t) => String(t.leave_type_id) === e.target.value,
                      );
                      if (type) setGrantDays(String(type.max_days_year));
                    }}
                  >
                    <option value="">Add a leave type</option>
                    {leaveTypes
                      .filter((t) => t.is_active && !assignedTypeIds.has(t.leave_type_id))
                      .map((t) => (
                        <option key={t.leave_type_id} value={t.leave_type_id}>
                          {t.name}
                        </option>
                      ))}
                  </AdminSoftSelect>
                  <AdminSoftInput
                    className="w-24"
                    type="number"
                    min={0}
                    placeholder="Days"
                    value={grantDays}
                    onChange={(e) => setGrantDays(e.target.value)}
                  />
                  <AdminPrimaryButton
                    disabled={!grantType || grantDays === ""}
                    onClick={grant}
                  >
                    Assign
                  </AdminPrimaryButton>
                </div>
                <div className="mt-4 space-y-2 border-t border-[#E8E8E4] pt-3">
                  <h3 className="text-xs font-semibold text-[#4A4A48]">Mark personal holiday</h3>
                  <p className="text-[11px] text-[#8A8A85]">
                    Deducts Holiday quota. Company national holidays are set on Organization.
                  </p>
                  <CustomDatePicker
                    label="Holiday date"
                    value={holidayDate}
                    onChange={setHolidayDate}
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={holidayHalf}
                      onChange={(e) => setHolidayHalf(e.target.checked)}
                    />
                    Half day
                  </label>
                  <AdminPrimaryButton disabled={!holidayDate} onClick={markHoliday}>
                    Mark holiday
                  </AdminPrimaryButton>
                </div>
              </>
            ) : null}
            {canMarkAttendance && applyEmp ? (
              <div className="mt-4 space-y-2 border-t border-[#E8E8E4] pt-3">
                <h3 className="text-xs font-semibold text-[#4A4A48]">Mark custom week off</h3>
                <p className="text-[11px] text-[#8A8A85]">
                  One-off week off for this employee (e.g. after double shift). Does not use leave
                  balance. Recurring offs are set on their profile Job tab.
                </p>
                <CustomDatePicker
                  label="Week off date"
                  value={weekOffDate}
                  onChange={setWeekOffDate}
                />
                <AdminSoftInput
                  placeholder="Note (optional)"
                  value={weekOffNote}
                  onChange={(e) => setWeekOffNote(e.target.value)}
                />
                <AdminPrimaryButton
                  disabled={!weekOffDate || weekOffBusy}
                  onClick={markCustomWeekOff}
                >
                  {weekOffBusy ? "Saving…" : "Mark week off"}
                </AdminPrimaryButton>
              </div>
            ) : null}
          </div>

          <div className="lg:col-span-2">
            <AdminTableCard>
              <table className="w-full min-w-[960px]">
                <thead>
                  <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                    <th className={adminTableHead}>Employee</th>
                    <th className={adminTableHead}>Type</th>
                    <th className={adminTableHead}>Requested on</th>
                    <th className={adminTableHead}>Leave dates</th>
                    <th className={adminTableHead}>Days</th>
                    <th className={adminTableHead}>Current balance</th>
                    <th className={adminTableHead}>Status</th>
                    <th className={`${adminTableHead} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && leaves.length === 0 ? (
                    <AdminTableState colSpan={8} variant="loading" message="Loading leaves…" />
                  ) : leaves.length === 0 ? (
                    <AdminTableState colSpan={8} variant="empty" message="No leave requests" />
                  ) : (
                    leaves.map((row) => {
                      const isHalfDay = Number(row.days) === 0.5;
                      const leaveDays =
                        Array.isArray(row.selected_dates) && row.selected_dates.length
                          ? [...row.selected_dates].sort()
                          : row.from_date === row.to_date
                            ? [row.from_date]
                            : [row.from_date, row.to_date];
                      const balanceLabel = row.balance
                        ? `${row.balance.remaining} left (${row.balance.allocated} allocated · ${row.balance.used} used · ${row.balance.pending} pending)`
                        : "—";
                      const target = {
                        role: row.Employee?.role,
                        emp_id: row.emp_id,
                      };
                      const canReview = canReviewLeaveOf(balanceActor, target);
                      const canCancel = canCancelLeaveOf(balanceActor, target);
                      const isOwn =
                        empId != null && Number(row.emp_id) === Number(empId);
                      return (
                      <tr key={row.leave_id} className="border-b border-[#F0F0EC]">
                        <td className={adminTableCell}>
                          {row.Employee?.emp_name || row.emp_id}
                          {isOwn ? (
                            <div className="mt-0.5 text-[11px] text-[#8A8A85]">Your request</div>
                          ) : null}
                        </td>
                        <td className={adminTableCell}>
                          {row.leaveType?.name}
                          {isHalfDay ? (
                            <div className="mt-0.5 text-[11px] font-medium text-[#345952]">
                              Half day
                            </div>
                          ) : null}
                        </td>
                        <td className={adminTableCell}>
                          <div className="font-medium text-[#4A4A48]">
                            {formatLeaveRequestedDate(row.created_at)}
                          </div>
                          <div className="mt-0.5 text-[11px] text-[#8A8A85]">
                            {formatLeaveRequestedTime(row.created_at)}
                          </div>
                        </td>
                        <td className={adminTableCell}>
                          <div className="flex flex-wrap gap-1">
                            {leaveDays.map((d) => (
                              <span
                                key={d}
                                className="rounded-full bg-[#E8F3EF] px-2 py-0.5 text-[11px] font-medium text-[#345952]"
                              >
                                {formatLeaveDate(d)}
                              </span>
                            ))}
                          </div>
                          {row.reason ? (
                            <div className="mt-1 rounded-lg bg-[#F8F8F6] px-2 py-1 text-[11px] text-[#4A4A48]">
                              Note: {row.reason}
                            </div>
                          ) : null}
                          {row.review_note ? (
                            <div className="mt-0.5 text-[11px] text-[#8A8A85]">
                              Reviewer: {row.review_note}
                            </div>
                          ) : null}
                        </td>
                        <td className={adminTableCell}>
                          {isHalfDay ? "0.5 (half day)" : row.days}
                        </td>
                        <td className={adminTableCell}>
                          <span className="text-[#4A4A48]">{balanceLabel}</span>
                        </td>
                        <td className={`${adminTableCell} capitalize`}>{row.status}</td>
                        <td className={`${adminTableCell} text-right`}>
                          <div className="flex justify-end gap-2">
                            {canReview && row.status === "pending" ? (
                              <>
                                <AdminPrimaryButton
                                  onClick={() =>
                                    dispatch(
                                      reviewHrLeave({ leave_id: row.leave_id, action: "approved" }),
                                    ).then(() => load())
                                  }
                                >
                                  Approve
                                </AdminPrimaryButton>
                                <button
                                  type="button"
                                  className="rounded-xl border border-red-200 px-3 py-2 text-xs text-red-600"
                                  onClick={() =>
                                    dispatch(
                                      reviewHrLeave({ leave_id: row.leave_id, action: "rejected" }),
                                    ).then(() => load())
                                  }
                                >
                                  Reject
                                </button>
                              </>
                            ) : null}
                            {canCancel && ["pending", "approved"].includes(row.status) ? (
                              <button
                                type="button"
                                className="rounded-xl border px-3 py-2 text-xs"
                                onClick={() =>
                                  dispatch(cancelHrLeave(row.leave_id)).then(() => load())
                                }
                              >
                                Cancel
                              </button>
                            ) : null}
                            {isOwn && !canReview && !canCancel ? (
                              <span className="text-[11px] text-[#8A8A85]">View only</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </AdminTableCard>

          </div>

          {canViewTeamLeaves ? (
            <div className="lg:col-span-3">
              <h2 className="mb-2 text-sm font-semibold text-[#4A4A48]">
                Mark present while on leave
              </h2>
              <p className="mb-3 text-xs text-[#8A8A85]">
                Same rules as leave requests: Admin can action all except own. Executive can action
                all except Admin and own. Super Admin can action all except own.
              </p>
              <AdminTableCard>
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                      <th className={adminTableHead}>Employee</th>
                      <th className={adminTableHead}>Role</th>
                      <th className={adminTableHead}>Day</th>
                      <th className={adminTableHead}>Status</th>
                      <th className={`${adminTableHead} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presentRequests.length === 0 ? (
                      <AdminTableState colSpan={5} variant="empty" message="No present requests" />
                    ) : (
                      presentRequests.map((row) => {
                        const canReviewPresent = canReviewLeaveOf(balanceActor, {
                          role: row.Employee?.role,
                          emp_id: row.emp_id,
                        });
                        return (
                        <tr key={row.request_id} className="border-b border-[#F0F0EC]">
                          <td className={adminTableCell}>{row.Employee?.emp_name || row.emp_id}</td>
                          <td className={adminTableCell}>{row.Employee?.role || "—"}</td>
                          <td className={adminTableCell}>{row.attendance_date}</td>
                          <td className={`${adminTableCell} capitalize`}>{row.status}</td>
                          <td className={`${adminTableCell} text-right`}>
                            {row.status === "pending" && canReviewPresent ? (
                              <div className="flex justify-end gap-2">
                                <AdminPrimaryButton
                                  onClick={() =>
                                    dispatch(
                                      reviewPresentOnLeave({
                                        request_id: row.request_id,
                                        action: "approved",
                                      }),
                                    ).then(() => load())
                                  }
                                >
                                  Approve
                                </AdminPrimaryButton>
                                <button
                                  type="button"
                                  className="rounded-xl border border-red-200 px-3 py-2 text-xs text-red-600"
                                  onClick={() =>
                                    dispatch(
                                      reviewPresentOnLeave({
                                        request_id: row.request_id,
                                        action: "rejected",
                                      }),
                                    ).then(() => load())
                                  }
                                >
                                  Reject
                                </button>
                              </div>
                            ) : row.status === "pending" ? (
                              <span className="text-[11px] text-[#8A8A85]">View only</span>
                            ) : (
                              row.reviewed_by || "—"
                            )}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </AdminTableCard>
            </div>
          ) : null}
        </div>
      </AdminPageShell>
    </>
  );
};

export default Leaves;
